import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  FormControlLabel,
  Switch,
  Checkbox,
  ListItemText,
  OutlinedInput,
  TablePagination,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material'
import {
  People,
  Edit,
  Delete,
  Search,
  Add,
  Lock,
  LockOpen,
  Visibility,
  VisibilityOff,
  LockReset,
  Computer,
  Person,
  PictureAsPdf,
  History
} from '@mui/icons-material'
import axios from 'axios'
import SesionesActivasDialog from './SesionesActivasDialog'
import WhoisOnline from './WhoisOnline'
import ActividadUsuario from './ActividadUsuario'

const UsuariosAdmin = () => {
  const [currentTab, setCurrentTab] = useState(0)
  const [usuarios, setUsuarios] = useState([])
  const [gestiones, setGestiones] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalUsuarios, setTotalUsuarios] = useState(0)
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedUsuario, setSelectedUsuario] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  const [openSesionesDialog, setOpenSesionesDialog] = useState(false)
  const [selectedUsuarioSesiones, setSelectedUsuarioSesiones] = useState(null)
  const [openActividadDialog, setOpenActividadDialog] = useState(false)
  const [selectedUsuarioActividad, setSelectedUsuarioActividad] = useState(null)
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    rol: 'consultor',
    gestiones_seleccionadas: [], // Array de {gestion_id, puede_editar}
    activo: true,
    usar_password_default: false
  })

  useEffect(() => {
    loadData()
  }, [page, rowsPerPage, searchTerm])

  // Debounce para búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput)
      setPage(0) // Resetear a primera página en nueva búsqueda
    }, 500)
    return () => clearTimeout(timer)
  }, [searchInput])

  const loadData = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('sgc_token')

      // Preparar parámetros de búsqueda
      const params = new URLSearchParams({
        page: (page + 1).toString(), // Backend usa páginas base 1
        limit: rowsPerPage.toString()
      })

      if (searchTerm && searchTerm.trim()) {
        params.append('search', searchTerm.trim())
      }

      // Cargar usuarios con paginación
      const usuariosResponse = await axios.get(`/api/auth/usuarios?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      // El endpoint devuelve { users: [...], pagination: {...} }
      setUsuarios(usuariosResponse.data.users || usuariosResponse.data)

      if (usuariosResponse.data.pagination) {
        setTotalUsuarios(usuariosResponse.data.pagination.total)
      } else {
        setTotalUsuarios(usuariosResponse.data.length)
      }

      // Cargar gestiones solo la primera vez
      if (gestiones.length === 0) {
        const gestionesResponse = await axios.get('/api/gestiones', {
          headers: { Authorization: `Bearer ${token}` }
        })
        setGestiones(gestionesResponse.data)
      }
    } catch (error) {
      setError('Error al cargar datos: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setSelectedUsuario(null)
    setFormData({
      nombre: '',
      email: '',
      password: '',
      rol: 'consultor',
      gestiones_seleccionadas: [],
      activo: true,
      usar_password_default: false
    })
    setOpenDialog(true)
  }

  const handleEdit = (usuario) => {
    setSelectedUsuario(usuario)

    // Convertir gestiones del usuario al formato del formulario
    const gestionesFormato = usuario.gestiones ?
      usuario.gestiones.map(g => ({
        gestion_id: g.id,
        puede_editar: g.puede_editar
      })) : []

    setFormData({
      nombre: usuario.nombre,
      email: usuario.email,
      password: '',
      rol: usuario.rol,
      gestiones_seleccionadas: gestionesFormato,
      activo: usuario.activo
    })
    setOpenDialog(true)
  }

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('sgc_token')

      // Preparar datos para el nuevo formato
      const saveData = {
        nombre: formData.nombre,
        email: formData.email,
        rol: formData.rol,
        activo: formData.activo,
        gestiones: formData.gestiones_seleccionadas,
        usar_password_default: formData.usar_password_default
      }

      // Agregar contraseña solo si se proporciona y no se usa la por defecto
      if (formData.password && formData.password.trim() && !formData.usar_password_default) {
        saveData.password = formData.password
      }

      if (selectedUsuario) {
        // Editar usando endpoint para múltiples gestiones
        await axios.put(`/api/auth/usuarios-multi/${selectedUsuario.id}`, saveData, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setSuccess('Usuario actualizado exitosamente')
      } else {
        // Crear usando endpoint para múltiples gestiones
        if (!saveData.password && !saveData.usar_password_default) {
          setError('Debe proporcionar una contraseña o usar la contraseña por defecto del sistema')
          return
        }
        await axios.post('/api/auth/usuarios-multi', saveData, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setSuccess('Usuario creado exitosamente')
      }

      setOpenDialog(false)
      loadData()
    } catch (error) {
      setError('Error al guardar: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleDelete = async (usuario) => {
    if (!window.confirm(`¿Está seguro de eliminar el usuario "${usuario.nombre}"?`)) {
      return
    }

    try {
      const token = localStorage.getItem('sgc_token')
      await axios.delete(`/api/auth/usuarios/${usuario.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('Usuario eliminado exitosamente')
      loadData()
    } catch (error) {
      setError('Error al eliminar: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleToggleActive = async (usuario) => {
    try {
      const token = localStorage.getItem('sgc_token')
      await axios.put(`/api/auth/usuarios/${usuario.id}`,
        { ...usuario, activo: !usuario.activo }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess(`Usuario ${!usuario.activo ? 'activado' : 'desactivado'} exitosamente`)
      loadData()
    } catch (error) {
      setError('Error al cambiar estado: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleResetPassword = async (usuario) => {
    if (!window.confirm(`¿Está seguro de reiniciar la contraseña de "${usuario.nombre}" a la contraseña por defecto del sistema?`)) {
      return
    }

    try {
      const token = localStorage.getItem('sgc_token')
      await axios.post(`/api/auth/usuarios/${usuario.id}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('Contraseña reiniciada exitosamente a la contraseña por defecto del sistema')
    } catch (error) {
      setError('Error al reiniciar contraseña: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get('/api/export/usuarios/list/pdf', {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      })

      // Crear un blob URL y descargarlo
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      // Generar nombre del archivo con fecha
      const fecha = new Date().toISOString().slice(0, 19).replace(/:/g, '-').replace('T', '_')
      link.download = `Usuarios_Sistema_${fecha}.pdf`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      setSuccess('PDF generado exitosamente')
    } catch (error) {
      setError('Error al generar PDF: ' + (error.response?.data?.detail || error.message))
    }
  }

  const getRolColor = (rol) => {
    switch (rol) {
      case 'administrador': return 'error'
      case 'rector': return 'secondary'
      case 'editor': return 'warning'
      case 'consultor': return 'info'
      default: return 'default'
    }
  }

  const getGestionNombre = (gestionId) => {
    const gestion = gestiones.find(g => g.id === gestionId)
    return gestion ? `${gestion.codigo} - ${gestion.nombre}` : 'Sin gestión'
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleVerSesiones = (usuario) => {
    setSelectedUsuarioSesiones(usuario)
    setOpenSesionesDialog(true)
  }

  const handleVerActividad = (usuario) => {
    setSelectedUsuarioActividad(usuario)
    setOpenActividadDialog(true)
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <People sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Gestión de Usuarios
          </Typography>
        </Box>
        {currentTab === 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdf />}
              onClick={handleExportPDF}
              color="error"
            >
              Exportar PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreate}
            >
              Nuevo Usuario
            </Button>
          </Box>
        )}
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab label="Usuarios" icon={<People />} iconPosition="start" />
          <Tab label="Usuarios en Línea" icon={<Person />} iconPosition="start" />
        </Tabs>
      </Paper>

      {currentTab === 1 ? (
        <WhoisOnline />
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Buscar usuarios por nombre, email o rol..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: loading && searchInput && (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            )
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Usuario</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Rol</TableCell>
              <TableCell>Gestión</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Último Acceso</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && usuarios.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Cargando usuarios...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : usuarios.map((usuario) => (
              <TableRow key={usuario.id} hover>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {usuario.nombre}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {usuario.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={usuario.rol.toUpperCase()}
                    color={getRolColor(usuario.rol)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {usuario.gestiones && usuario.gestiones.length > 0 ? (
                      usuario.gestiones.map((gestion) => (
                        <Chip
                          key={gestion.id}
                          label={gestion.codigo}
                          size="small"
                          color={gestion.puede_editar ? "primary" : "default"}
                          variant={gestion.puede_editar ? "filled" : "outlined"}
                          sx={gestion.puede_editar ? {
                            backgroundColor: gestion.color || '#64B5F6',
                            color: '#fff',
                            border: `1px solid ${gestion.color || '#64B5F6'}`,
                            fontWeight: 'bold'
                          } : {
                            fontWeight: 'bold'
                          }}
                          title={`${gestion.nombre} ${gestion.puede_editar ? '(Puede editar)' : '(Solo lectura)'}`}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Sin gestión
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={usuario.activo ? 'Activo' : 'Inactivo'}
                    color={usuario.activo ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {usuario.ultimo_login ?
                      new Date(usuario.ultimo_login).toLocaleDateString() :
                      'Nunca'
                    }
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(usuario)}
                      title="Editar"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleVerSesiones(usuario)}
                      title="Ver sesiones activas"
                      color="info"
                    >
                      <Computer />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleVerActividad(usuario)}
                      title="Ver actividad del usuario"
                      color="secondary"
                    >
                      <History />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleResetPassword(usuario)}
                      title="Reiniciar contraseña"
                      color="primary"
                    >
                      <LockReset />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleActive(usuario)}
                      title={usuario.activo ? 'Desactivar' : 'Activar'}
                      color={usuario.activo ? 'warning' : 'success'}
                    >
                      {usuario.activo ? <Lock /> : <LockOpen />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(usuario)}
                      title="Eliminar"
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {!loading && usuarios.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm ? 'No se encontraron usuarios con ese criterio de búsqueda' : 'No hay usuarios registrados'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalUsuarios}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          labelRowsPerPage="Usuarios por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </TableContainer>

      {/* Dialog para crear/editar */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedUsuario ? 'Editar Usuario' : 'Nuevo Usuario'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Nombre completo"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              required
            />
            
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            
            <TextField
              fullWidth
              label={selectedUsuario ? 'Nueva contraseña (opcional)' : 'Contraseña'}
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!selectedUsuario && !formData.usar_password_default}
              disabled={formData.usar_password_default}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />

            {!selectedUsuario && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.usar_password_default}
                    onChange={(e) => setFormData({
                      ...formData,
                      usar_password_default: e.target.checked,
                      password: e.target.checked ? '' : formData.password
                    })}
                  />
                }
                label="Usar contraseña por defecto del sistema"
              />
            )}

            <FormControl fullWidth>
              <InputLabel>Rol</InputLabel>
              <Select
                value={formData.rol}
                label="Rol"
                onChange={(e) => setFormData({ ...formData, rol: e.target.value })}
              >
                <MenuItem value="consultor">Consultor</MenuItem>
                <MenuItem value="editor">Editor</MenuItem>
                <MenuItem value="rector">Rector</MenuItem>
                <MenuItem value="administrador">Administrador</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Gestiones Asignadas
              </Typography>
              {gestiones.map((gestion) => {
                const isSelected = formData.gestiones_seleccionadas.some(
                  g => g.gestion_id === gestion.id
                )
                const selectedGestion = formData.gestiones_seleccionadas.find(
                  g => g.gestion_id === gestion.id
                )

                return (
                  <Box
                    key={gestion.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.5,
                      px: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Agregar gestión
                            setFormData({
                              ...formData,
                              gestiones_seleccionadas: [
                                ...formData.gestiones_seleccionadas,
                                { gestion_id: gestion.id, puede_editar: true }
                              ]
                            })
                          } else {
                            // Quitar gestión
                            setFormData({
                              ...formData,
                              gestiones_seleccionadas: formData.gestiones_seleccionadas.filter(
                                g => g.gestion_id !== gestion.id
                              )
                            })
                          }
                        }}
                      />
                      <ListItemText
                        primary={`${gestion.codigo} - ${gestion.nombre}`}
                        secondary={gestion.descripcion}
                      />
                    </Box>
                    {isSelected && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedGestion?.puede_editar || false}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                gestiones_seleccionadas: formData.gestiones_seleccionadas.map(g =>
                                  g.gestion_id === gestion.id
                                    ? { ...g, puede_editar: e.target.checked }
                                    : g
                                )
                              })
                            }}
                            size="small"
                          />
                        }
                        label="Puede editar"
                        sx={{ mr: 0 }}
                      />
                    )}
                  </Box>
                )
              })}
              {gestiones.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No hay gestiones disponibles
                </Typography>
              )}
            </FormControl>
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.activo}
                  onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                />
              }
              label="Usuario activo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

          {/* Dialog para sesiones activas */}
          <SesionesActivasDialog
            open={openSesionesDialog}
            onClose={() => setOpenSesionesDialog(false)}
            usuario={selectedUsuarioSesiones}
          />

          {/* Dialog para actividad del usuario */}
          <Dialog
            open={openActividadDialog}
            onClose={() => setOpenActividadDialog(false)}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <History />
                Actividad del Usuario
              </Box>
            </DialogTitle>
            <DialogContent>
              {selectedUsuarioActividad && (
                <ActividadUsuario
                  usuarioId={selectedUsuarioActividad.id}
                  usuarioNombre={selectedUsuarioActividad.nombre}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenActividadDialog(false)}>
                Cerrar
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  )
}

export default UsuariosAdmin